import weekday from 'dayjs/plugin/weekday.js'
import utc from 'dayjs/plugin/utc.js'
import timezone from 'dayjs/plugin/timezone.js' // dependent on utc plugin
import relativeTime from 'dayjs/plugin/relativeTime.js'
import duration from 'dayjs/plugin/duration.js'
import dayjs from 'dayjs'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(weekday)
dayjs.extend(relativeTime)
dayjs.extend(duration)

const isMaDate = d => d instanceof MaDate

/**
 * @description Dayjs date wrapper.
 * @param {string | MaDate} date (optional) If not passed date, it gets current date.
 * @param {string} format (optional) date format
 * @returns {MaDate | string | number} MaDate | String | Number
 */
function maDate(date = undefined, format = undefined) {
  return new MaDate(date, format)
}

maDate.isMaDate = isMaDate

class MaDate {
  constructor(date, format) {
    this.dayjs = dayjs(date, format)
    this._bootstrap()
  }

  get dayjs() {
    return this._dayjs
  }

  set dayjs(value) {
    const hasChanged = (this._dayjs !== value)
    this._dayjs = value

    if (hasChanged) {
      this._onPropertyChanged()
    }
  }

  _bootstrap() {
    const { $d } = this.dayjs
    this._d = $d
    this._y = $d.getFullYear()
    this._M = $d.getMonth()
    this._D = $d.getDate()
    this._W = $d.getDay()
    this._H = $d.getHours()
    this._m = $d.getMinutes()
    this._s = $d.getSeconds()
    this._ms = $d.getMilliseconds()
  }

  /**
   * @description This returns a Day.js object with a flag to use UTC time.
   * Passing true will change the time zone without changing the current time.
   * ```
   * dayjs('2016-05-03 22:15:01').utc(true).format() // 2016-05-03T22:15:01Z
   * ```
   * @param {boolean} keepLocalTime
   * @returns {MaDate} MaDate
   */
  utc(keepLocalTime) {
    this.dayjs = this.dayjs.utc(keepLocalTime)
    return this
  }

  formatToUTC(keepLocalTime = true) {
    return dayjs(this.dayjs).utc(keepLocalTime).format()
  }

  /**
   * @description Change default timezone from local time zone to your custom timezone.
   * ```
   * // Converting (from time zone 'Europe/Berlin'!)
   * dayjs("2022-11-25 11:55:20").tz("America/Toronto") // '2022-11-25T05:55:20-05:00'
   * ```
   * @param {string} timezone
   * @param {boolean} keepLocalTime
   * @returns {MaDate} MaDate
   */
  tz(timezone, keepLocalTime) {
    this.dayjs = this.dayjs.tz(timezone, keepLocalTime)
    return this
  }

  /**
   * @description Get the date of the month.
   * ```
   * maDate().date()// => 1-31
   * ```
   * Docs: https://day.js.org/docs/en/get-set/date
   * @returns {number}
   */
  date() {
    return this.dayjs.date()
  }

  /**
   * @description To get a copy of the native Date object parsed from the Day.js object use.
   * ```
   * Only native Date class chained.
   *
   * maDate('2019-01-25').toDate()// => Date
   * ```
   * @returns {Date} Date
   */
  toDate() {
    return this.dayjs.toDate()
  }

  /**
   * @description Weekday adds .weekday() API to get or set locale aware day of the week.
   * ```
   * // when Monday is the first day of the week
   * maDate().weekday(-7) // last Monday
   * maDate().weekday(7) // next Monday
   * ```
   * @param {number} value given a value between 1-7 or negative
   */
  weekday(value) {
    this.dayjs = this.dayjs.weekday(value)
    return this
  }

  /**
   * @description Get the formatted date according to the string of tokens passed in.
   * To escape characters, wrap them in square brackets (e.g. [MM]).
   * ```
   * Cannot be chained.
   *
   * maDate('2022-11-25').format('DD/MM/YYYY') // '25/11/2022'
   * ```
   * @param {string} template date format template
   * @param {boolean} keepLocalTime
   * @returns {string} formatted date: e.g. 2022-11-25
   */
  format(template = undefined, keepLocalTime = false) {
    return keepLocalTime
      ? dayjs(this.formatToUTC(keepLocalTime)).format(template)
      : this.dayjs.format(template)
  }

  /**
   * @description Get the human-readable format date.
   * If withTime is passed true, the time is returned along with the date.
   * ```
   * Cannot be chained.
   *
   * maDate('2022-11-25').formatHumanReadable() // Nov 25, 2022
   * maDate('2022-11-25').formatHumanReadable(true) // Nov 25, 2022 8:02 PM
   * ```
   * @param {boolean} withTime for along time
   * @param {boolean} keepLocalTime
   * @returns {string} formatted date: e.g. Nov 25, 2022
   */
  formatHumanReadable(withTime = false, keepLocalTime = false) {
    const dateFormatString = 'MMM DD, YYYY'
    const format = withTime ? `${dateFormatString}, hh:mm` : dateFormatString
    return keepLocalTime
      ? dayjs(this.formatToUTC(keepLocalTime)).format(format)
      : this.dayjs.format(format)
  }

  /**
   * @description Returns the string of relative time from now.
   * ```
   * Cannot be chained.
   *
   * maDate('2022-11-25').fromNow() // one year ago
   * ```
   * @param {boolean} withoutSuffix If you pass true, you can get the value without the suffix. e.g. one year
   * @returns {string} formatted date: e.g. one year ago or one year
   */
  fromNow(withoutSuffix = false) {
    return this.dayjs.fromNow(withoutSuffix)
  }

  /**
   * @description Returns the string in ISO date format. This method is generally used for the expected date format of the HTTP request.
   * ```
   * Cannot be chained.
   *
   * maDate('2022-11-25').formatISODate() // 2022-11-25
   * ```
   * @returns {string} formatted date: e.g. 2022-11-25
   */
  formatISODate() {
    return this.dayjs.format('YYYY-MM-DD')
  }

  /**
   * @description This returns the number of milliseconds since the Unix Epoch of the Day.js object.
   * ```
   * Cannot be chained.
   * maDate('2022-11-25').valueOf() // 1548381600000
   * ```
   * @returns {number} Number
   */
  valueOf() {
    return this.dayjs.valueOf()
  }

  /**
   * @description This returns the Unix timestamp (the number of seconds since the Unix Epoch) of the Day.js object.
   * ```
   * Cannot be chained.
   * maDate('2022-11-25').unix() // 1548381600
   * ```
   * @returns {number} Number
   */
  unix() {
    return this.dayjs.unix()
  }

  /**
   * @description Gets or sets the minutes.
   * Accepts numbers from 0 to 59. If the range is exceeded, it will bubble up to the hour.
   * ```
   * maDate().minute() // gets current minute
   * maDate().minute(59) // returns new maDate object
   * ```
   * @param {number} value minute range
   */
  minute(value = undefined) {
    if (value === undefined) {
      return this.dayjs.minute()
    }

    this.dayjs = this.dayjs.minute(value)
    return this
  }

  /**
   * @description Returns a cloned Day.js object with a specified amount of time added.
   * ```
   * maDate().add(7, 'day')// => MaDate
   * ```
   * @param {number} amount
   * @param {string} unit
   * @returns MaDate
   */
  add(amount, unit) {
    this.dayjs = this.dayjs.add(amount, unit)
    return this
  }

  /**
   * @description Returns a cloned Day.js object with a specified amount of time subtracted.
   * ```
   * maDate().subtract(7, 'year')// => MaDate
   * ```
   * @param {number} amount
   * @param {string} unit
   * @returns MaDate
   */
  subtract(amount, unit) {
    this.dayjs = this.dayjs.subtract(amount, unit)
    return this
  }

  /**
   * @description Returns a cloned Day.js object and set it to the start of a unit of time.
   * ```
   * maDate().startOf('year')// => MaDate
   * ```
   * Units are case insensitive, and support plural and short forms.
   * @param {string} unit
   * @returns MaDate
   */
  startOf(unit) {
    this.dayjs = this.dayjs.startOf(unit)
    return this
  }

  /**
   * @description Returns a cloned Day.js object and set it to the end of a unit of time.
   * ```
   * maDate().endOf('year')// => MaDate
   * ```
   * Units are case insensitive, and support plural and short forms.
   * @param {string} unit
   * @returns MaDate
   */
  endOf(unit) {
    this.dayjs = this.dayjs.endOf(unit)
    return this
  }

  /**
   * @description This indicates the difference between two date-time in the specified unit.
   * ```
   * Cannot be chained.
   *
   * const date1 = maDate('2019-01-25')
   * const date2 = maDate('2018-06-05')
   * date1.diff(date2) // 20214000000 default milliseconds
   * date1.diff() // milliseconds to current time
   * ```
   * @param {String || MaDate} date
   * @param {string} unit
   * @param {boolean} float
   * @returns Number
   */
  diff(date, unit, float = false) {
    const dateInstance = isMaDate(date) ? dayjs(date.dayjs) : date
    return this.dayjs.diff(dateInstance, unit, float)
  }

  /**
   * @description Returns a boolean indicating whether the maDate's date is valid.
   * Only checks if the value could be parsed to a Date time.
   * @returns Boolean
   */
  isValid() {
    return this.dayjs.isValid()
  }

  duration(date) {
    return this.dayjs.duration(date)
  }

  year() {
    return this.dayjs.year()
  }

  month() {
    return this.dayjs.month()
  }

  convertToLocalTime(utcDateString) {
    if (!utcDateString || !dayjs.utc(utcDateString).isValid()) {
      return null
    }

    this.dayjs = dayjs.utc(utcDateString).local()

    return this
  }

  _onPropertyChanged() {
    this._bootstrap()
  }
}

export default maDate
